export enum OrgAdminRole {
  OWNER = "OWNER",
  ADMIN = "ADMIN",
}

export enum SportType {
  PETANQUE = "PETANQUE",
  LYONNAISE = "LYONNAISE",
  PROVENCAL = "PROVENCAL",
  TOUS = "TOUS",
}

export enum CompetitionType {
  SINGLE = "SINGLE",
  DOUBLE = "DOUBLE",
  TRIPLE = "TRIPLE",
}
export enum CompositionType {
  FORMEE = "FORMEE",
  MELEE = "MELEE",
  FORMEE_ALEATOIRE = "FORMEE_ALEATOIRE",
}

export enum CompetitionLevel {
  LOCAL = "LOCAL",
  DEPARTMENTAL = "DEPARTMENTAL",
  REGIONAL = "REGIONAL",
  NATIONAL = "NATIONAL",
  CHAMPIONSHIP = "CHAMPIONSHIP",
  PROMOTION = "PROMOTION",
  MASTER = "MASTER",
  PRESTIGE = "PRESTIGE",
}

export enum CompetitionStatus {
  DRAFT = "DRAFT",
  REGISTRATION_OPEN = "REGISTRATION_OPEN",
  REGISTRATION_CLOSED = "REGISTRATION_CLOSED",
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
  CANCELLED = "CANCELLED",
  DISABLED_BY_ADMIN = "DISABLED_BY_ADMIN",
}

export enum TeamRegistrationStatus {
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  REJECTED = "REJECTED",
  CANCELLED = "CANCELLED",
}

export enum PaymentStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  REFUNDED = "REFUNDED",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED", // P3b-PR1: refunds partiels
}

// Migration Direct Charges 2026-06 : DESTINATION = paiements legacy (charge
// plateforme + transfert), DIRECT = charge sur le compte connecté de l'orga.
// Détermine le chemin de refund (cf. payments.service.refund).
export enum ChargeMode {
  DESTINATION = "DESTINATION",
  DIRECT = "DIRECT",
}

// Sprint C-bis: workflow demande de remboursement
export enum RefundRequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

// 0€/mois + 2% comm
// 80€/mois + 0% (anciennement PRO)
export enum SubPlan {
  COMMISSION = "COMMISSION",
  FORFAIT = "FORFAIT",
}

// Free-1€/mois
export enum UserPlan {
  FREE = "FREE",
  PLUS = "PLUS",
}

// Free-20€/mois
export enum ClubPlan {
  FREE = "FREE",
  PRO = "PRO",
}

// Volontairement `const object + type literal union` (et non `enum`) pour
// rester structurellement compatible avec l'enum `SubscriptionStatus` généré
// par Prisma côté API (`@prisma/client`). Un `enum` TS serait nominalement
// distinct malgré des valeurs identiques, ce qui casserait les helpers shared
// (cf. `premium.utils.ts` consommé par `teams.service.ts`). Aucune perte de
// strictness : le type reste l'union exacte des 4 littéraux autorisés.
export const SubscriptionStatus = {
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE",
  CANCELED: "CANCELED",
  UNPAID: "UNPAID",
} as const;
export type SubscriptionStatus =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export enum StripeAccountStatus {
  PENDING = "PENDING",
  ONBOARDING = "ONBOARDING",
  ACTIVE = "ACTIVE",
  RESTRICTED = "RESTRICTED",
  DISABLED = "DISABLED",
}

export enum FileUsage {
  USER_AVATAR = "USER_AVATAR",
  USER_COVER = "USER_COVER",
  ORG_LOGO = "ORG_LOGO",
  ORG_COVER = "ORG_COVER",
  CLUB_LOGO = "CLUB_LOGO",
  CLUB_COVER = "CLUB_COVER",
  COMPETITION_BANNER = "COMPETITION_BANNER",
  COMPETITION_COVER = "COMPETITION_COVER",
  COMPETITION_DOCUMENT = "COMPETITION_DOCUMENT",
  ARTICLE_COVER = "ARTICLE_COVER",
}

export enum ClubAffiliationRequestStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

export enum ClubMembershipRequestStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

export enum OrganizationAdminRequestStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

// ============================================
// FFPJP — Enums fédéraux
// ============================================

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
}

export enum FederalClassification {
  ELITE = "ELITE",
  HONNEUR = "HONNEUR",
  PROMOTION = "PROMOTION",
}

export enum FederalAgeCategory {
  VETERAN = "VETERAN",
  SENIOR = "SENIOR",
  JUNIOR = "JUNIOR",
  CADET = "CADET",
  MINIME = "MINIME",
  BENJAMIN = "BENJAMIN",
}

export enum AuthChallengeType {
  TOTP = "TOTP",
  EMAIL_OTP = "EMAIL_OTP",
  BACKUP_CODE = "BACKUP_CODE",
}

export enum MfaLevel {
  NONE = "none",
  EMAIL = "email",
  TOTP = "totp",
}

export enum NotificationType {
  TEAM_REGISTERED = "TEAM_REGISTERED",
  TEAM_CONFIRMED = "TEAM_CONFIRMED",
  TEAM_REJECTED = "TEAM_REJECTED",
  PAYMENT_CONFIRMED = "PAYMENT_CONFIRMED",
  PAYMENT_REFUNDED = "PAYMENT_REFUNDED",
  PAYMENT_PARTIALLY_REFUNDED = "PAYMENT_PARTIALLY_REFUNDED", // P3b-PR1: refunds partiels
  COMPETITION_RESULTS = "COMPETITION_RESULTS",
  MEMBER_REQUEST = "MEMBER_REQUEST",
  MEMBER_APPROVED = "MEMBER_APPROVED",
  MEMBER_REJECTED = "MEMBER_REJECTED",
  AFFILIATION_REQUEST = "AFFILIATION_REQUEST",
  AFFILIATION_APPROVED = "AFFILIATION_APPROVED",
  CLAIM_APPROVED = "CLAIM_APPROVED",
  CLAIM_REJECTED = "CLAIM_REJECTED",
  REFUND_REQUEST_CREATED = "REFUND_REQUEST_CREATED", // V1.1 — renommé depuis REFUND_REQUESTED (décision 2026-05-22)
  REFUND_APPROVED = "REFUND_APPROVED",
  REFUND_REJECTED = "REFUND_REJECTED",
  SUBSCRIPTION_PAYMENT_FAILED = "SUBSCRIPTION_PAYMENT_FAILED",
  SUBSCRIPTION_CANCELLED = "SUBSCRIPTION_CANCELLED",
  PAYMENT_DISPUTED = "PAYMENT_DISPUTED",
  COMPETITION_REMINDER_J7 = "COMPETITION_REMINDER_J7",
  COMPETITION_REMINDER_J1 = "COMPETITION_REMINDER_J1",
  ORG_ADMIN_REQUEST = "ORG_ADMIN_REQUEST",
  ORG_ADMIN_REQUEST_ACCEPTED = "ORG_ADMIN_REQUEST_ACCEPTED",
  ORG_ADMIN_REQUEST_REJECTED = "ORG_ADMIN_REQUEST_REJECTED",
}

// ============================================
// FFPJP — Sync & Club Admin Claim
// ============================================

export enum FfpjpSyncStatus {
  PENDING = "PENDING",
  SKIPPED = "SKIPPED",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export enum ClubAdminClaimStatus {
  PENDING = "PENDING",
  APPROVED_AUTO = "APPROVED_AUTO",
  APPROVED_MANUAL = "APPROVED_MANUAL",
  REJECTED = "REJECTED",
}

// ============================================
// ADMIN AUDIT LOG
// ============================================

export enum AdminAuditAction {
  IMPERSONATE_START = "IMPERSONATE_START",
  IMPERSONATE_STOP = "IMPERSONATE_STOP",
  SAAS_PARAMS_UPDATE = "SAAS_PARAMS_UPDATE",
  CLAIM_APPROVE = "CLAIM_APPROVE",
  CLAIM_REJECT = "CLAIM_REJECT",
  REFUND_APPROVE = "REFUND_APPROVE",
  REFUND_REJECT = "REFUND_REJECT",
  USER_ROLE_CHANGE = "USER_ROLE_CHANGE",
  ORG_ADMIN_REQUEST_ACCEPT = "ORG_ADMIN_REQUEST_ACCEPT",
  ORG_ADMIN_REQUEST_REJECT = "ORG_ADMIN_REQUEST_REJECT",
  ORG_ADMIN_PING = "ORG_ADMIN_PING",
  CMS_OFFER_CHANGE = "CMS_OFFER_CHANGE",
  CMS_DOMAIN_CHANGE = "CMS_DOMAIN_CHANGE",
}

// ============================================
// REFERRAL REWARD
// ============================================

export enum ReferralRewardStatus {
  PENDING = "PENDING",
  GRANTED = "GRANTED",
}

// ============================================
// BLOG
// ============================================

export enum ArticleStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
}

// ============================================
// FEEDBACK (widget retours utilisateurs — V1.0)
// ============================================

export enum FeedbackType {
  BUG = "BUG",
  AVIS = "AVIS",
  SUGGESTION = "SUGGESTION",
}

export enum FeedbackStatus {
  NEW = "NEW",
  IN_REVIEW = "IN_REVIEW",
  RESOLVED = "RESOLVED",
  ARCHIVED = "ARCHIVED",
}

export enum FeedbackSource {
  FRONT = "FRONT",
  ADMIN = "ADMIN",
}
